import React, { useEffect, useState, useCallback } from 'react';
import { ownerPortal, ownerPayments } from '../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';

// ─── Stripe payment form (inside Elements provider) ───────────────────────────
function CheckoutForm({ paymentId, feeIds, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setErrorMsg('');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        setErrorMsg(error.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // Tell backend to mark fees paid and send receipt
        await ownerPayments.confirm({
          payment_intent_id: paymentIntent.id,
          fee_ids: feeIds,
        });
        toast.success('Payment successful! Receipt sent to your email.');
        onSuccess();
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {errorMsg && (
        <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">
          {errorMsg}
        </div>
      )}
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel}
          className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-medium">
          Cancel
        </button>
        <button type="submit" disabled={!stripe || processing}
          className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-60">
          {processing ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Processing…
            </span>
          ) : 'Confirm Payment'}
        </button>
      </div>
      <p className="text-xs text-gray-400 text-center">
        🔒 Secured by Stripe · Your card details are never stored on our servers
      </p>
    </form>
  );
}

// ─── Main Payments Page ───────────────────────────────────────────────────────
export default function OwnerPayments() {
  const [fees, setFees] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('outstanding'); // outstanding | history
  const [selectedFees, setSelectedFees] = useState([]);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentId, setPaymentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [feesRes, histRes, configRes] = await Promise.all([
        ownerPortal.fees({ status: 'pending' }),
        ownerPayments.history(),
        ownerPayments.config(),
      ]);
      setFees(feesRes.data);
      setHistory(histRes.data);
      if (configRes.data.publishable_key) {
        setStripePromise(loadStripe(configRes.data.publishable_key));
      }
    } catch {
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleFee = (id) => {
    setSelectedFees((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectedTotal = fees
    .filter((f) => selectedFees.includes(f.id))
    .reduce((sum, f) => sum + f.amount, 0);

  const handlePayNow = async () => {
    if (selectedFees.length === 0) {
      toast.error('Select at least one fee to pay');
      return;
    }
    if (!stripePromise) {
      toast.error('Stripe is not configured. Please contact your strata manager.');
      return;
    }
    setCreating(true);
    try {
      const res = await ownerPayments.createIntent({ fee_ids: selectedFees });
      setClientSecret(res.data.client_secret);
      setPaymentId(res.data.payment_id);
      setShowCheckout(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to initialize payment');
    } finally {
      setCreating(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowCheckout(false);
    setClientSecret('');
    setSelectedFees([]);
    load(); // refresh fees and history
    setTab('history');
  };

  const selectAll = () => setSelectedFees(fees.map((f) => f.id));
  const clearAll = () => setSelectedFees([]);

  const FEE_STATUS = {
    pending: 'bg-yellow-100 text-yellow-800',
    overdue: 'bg-red-100 text-red-800',
    paid: 'bg-green-100 text-green-800',
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Payments</h1>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {[['outstanding', 'Outstanding Fees'], ['history', 'Payment History']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              tab === key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {label}
            {key === 'outstanding' && fees.length > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                {fees.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Payment modal */}
      {showCheckout && clientSecret && stripePromise && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1">Complete Payment</h2>
            <p className="text-gray-500 text-sm mb-6">
              Total: <strong className="text-gray-900">${selectedTotal.toFixed(2)} CAD</strong>
            </p>
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: { theme: 'stripe', variables: { colorPrimary: '#2563eb' } },
              }}>
              <CheckoutForm
                paymentId={paymentId}
                feeIds={selectedFees}
                onSuccess={handlePaymentSuccess}
                onCancel={() => { setShowCheckout(false); setClientSecret(''); }}
              />
            </Elements>
          </div>
        </div>
      )}

      {/* Stripe not configured banner */}
      {!stripePromise && tab === 'outstanding' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-800">
          ⚠️ Online payments are not yet configured. Please contact your strata manager or pay by cheque.
        </div>
      )}

      {/* Outstanding Fees Tab */}
      {tab === 'outstanding' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          {fees.length === 0 ? (
            <div className="py-16 text-center">
              <span className="text-4xl">🎉</span>
              <p className="mt-3 text-lg font-medium text-gray-900">All caught up!</p>
              <p className="text-gray-500 text-sm">No outstanding fees.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <button onClick={selectAll} className="text-xs text-blue-600 hover:underline">Select all</button>
                  <button onClick={clearAll} className="text-xs text-gray-500 hover:underline">Clear</button>
                </div>
                {selectedFees.length > 0 && (
                  <span className="text-sm font-medium text-gray-700">
                    {selectedFees.length} selected · ${selectedTotal.toFixed(2)}
                  </span>
                )}
              </div>

              <ul className="divide-y divide-gray-100">
                {fees.map((fee) => (
                  <li key={fee.id}
                    onClick={() => toggleFee(fee.id)}
                    className={`flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedFees.includes(fee.id) ? 'bg-blue-50' : ''
                    }`}>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      selectedFees.includes(fee.id)
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300'
                    }`}>
                      {selectedFees.includes(fee.id) && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{fee.period_label || fee.description}</p>
                      <p className="text-sm text-gray-500">Due {fee.due_date}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${FEE_STATUS[fee.status] || ''}`}>
                        {fee.status}
                      </span>
                      <span className="font-bold text-gray-900">${fee.amount.toFixed(2)}</span>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
                <button
                  onClick={handlePayNow}
                  disabled={selectedFees.length === 0 || creating || !stripePromise}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50 transition-colors">
                  {creating ? 'Preparing…' : selectedFees.length === 0
                    ? 'Select fees to pay'
                    : `Pay $${selectedTotal.toFixed(2)} Now`}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Payment History Tab */}
      {tab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          {history.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <p>No payment history yet</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {history.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-5 py-4">
                  <div>
                    <p className="font-medium text-gray-900">{p.description || 'Strata fee'}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(p.created_at).toLocaleDateString('en-CA', {
                        year: 'numeric', month: 'long', day: 'numeric'
                      })}
                      {' · '}{p.payment_method?.replace('_', ' ') || 'card'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {p.receipt_sent && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Receipt sent
                      </span>
                    )}
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                      Paid
                    </span>
                    <span className="font-bold text-green-700">${p.amount.toFixed(2)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
